/**
 * LocalStorage wrapper for widget persistence
 */

const STORAGE_PREFIX = 'sitegpt_';

export interface StoredMessage {
  id: string;
  sendBy: 'user' | 'bot';
  message: string;
  createdAt: string;
}

/**
 * Get conversation ID for a bot
 */
export function getConversationId(botId: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}conv_${botId}`);
  } catch {
    return null;
  }
}

/**
 * Set conversation ID for a bot
 */
export function setConversationId(botId: string, conversationId: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}conv_${botId}`, conversationId);
  } catch {
    // Storage may be full or disabled
  }
}

/**
 * Get cached messages for a bot
 */
export function getMessages(botId: string): StoredMessage[] {
  try {
    const data = localStorage.getItem(`${STORAGE_PREFIX}msgs_${botId}`);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

/**
 * Add message to local cache
 */
export function addMessage(botId: string, message: StoredMessage): void {
  try {
    const messages = getMessages(botId);
    messages.push(message);
    // Keep last 50 messages to avoid storage bloat
    const trimmed = messages.slice(-50);
    localStorage.setItem(`${STORAGE_PREFIX}msgs_${botId}`, JSON.stringify(trimmed));
  } catch {
    // Storage may be full or disabled
  }
}

/**
 * Update the last message (for streaming updates)
 */
export function updateLastMessage(botId: string, message: string): void {
  try {
    const messages = getMessages(botId);
    if (messages.length > 0) {
      messages[messages.length - 1].message = message;
      localStorage.setItem(`${STORAGE_PREFIX}msgs_${botId}`, JSON.stringify(messages));
    }
  } catch {
    // Storage may be full or disabled
  }
}

/**
 * Set messages cache (for restoring from server)
 */
export function setMessages(botId: string, messages: StoredMessage[]): void {
  try {
    // Keep last 50 messages
    const trimmed = messages.slice(-50);
    localStorage.setItem(`${STORAGE_PREFIX}msgs_${botId}`, JSON.stringify(trimmed));
  } catch {
    // Storage may be full or disabled
  }
}

/**
 * Clear conversation data for a bot
 */
export function clearConversation(botId: string): void {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}conv_${botId}`);
    localStorage.removeItem(`${STORAGE_PREFIX}msgs_${botId}`);
    localStorage.removeItem(`${STORAGE_PREFIX}lead_${botId}`);
  } catch {
    // Ignore errors
  }
}

/**
 * Get lead ID for a bot
 */
export function getLeadId(botId: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}lead_${botId}`);
  } catch {
    return null;
  }
}

/**
 * Set lead ID for a bot
 */
export function setLeadId(botId: string, leadId: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}lead_${botId}`, leadId);
  } catch {
    // Storage may be full or disabled
  }
}

/**
 * Check if lead form has been completed
 */
export function hasCompletedLeadForm(botId: string): boolean {
  return getLeadId(botId) !== null;
}

/**
 * Get widget state (open/closed)
 */
export function getWidgetState(botId: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}open_${botId}`) === 'true';
  } catch {
    return false;
  }
}

/**
 * Set widget state (open/closed)
 */
export function setWidgetState(botId: string, isOpen: boolean): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}open_${botId}`, String(isOpen));
  } catch {
    // Ignore errors
  }
}
